import { c as _c } from "react/compiler-runtime";
/**
 * `useGetConnection()` — consumer-facing connection state hook.
 *
 * App code calls this hook to read the 4-state connectivity FSM,
 * re-renders on every transition, and triggers manual reconnects.
 *
 * Locked return shape (do NOT extend without a public-surface review):
 *
 *   { state, cause, queuedMutations, forceReconnect }
 *
 * Notes:
 *   - FSM lives at `connectivity/state-machine.ts` (`subscribe` +
 *     `getSnapshot`). The hook adapts via `useSyncExternalStore` — the
 *     FSM stays framework-free.
 *   - `queuedMutations` aggregates across all mutation scopes via
 *     TanStack's `useMutationState({ filters: { status: 'pending' } })`.
 *   - `forceReconnect` is debounced to 1 call per 300ms (RECONNECT_CLICK
 *     _DEBOUNCE_MS) and routed through `requestAction('reconnect', body)`
 *     to respect the refresh-vs-reconnect mutex. The body calls
 *     `nc.reconnect()` on the globally-pinned NATS client when one is
 *     present; absence is a no-op (pre-mint or after drain).
 */
import { useMutationState } from '@tanstack/react-query';
import * as React from 'react';
import { connectivityStore } from '../../core/connectivity-store.js';
import { requestAction } from '../fsm/connection-action.js';
/** Debounce window for `forceReconnect()` rapid clicks. */
export const RECONNECT_CLICK_DEBOUNCE_MS = 300;
const NATS_CLIENT_GLOBAL_KEY = '__morfi_natsClient';
/**
 * Resolve the globally-pinned NATS client (set by `connectSingleton` on
 * wsconnect resolve) and call its `reconnect()` method. Absence is a
 * no-op — the FSM will continue to drive the reconnect ladder via
 * `cold-connect-retry`.
 */
async function performTransportReconnect() {
  const nc = globalThis[NATS_CLIENT_GLOBAL_KEY];
  if (nc?.reconnect === undefined) return;
  await nc.reconnect();
}
let cachedSnapshot = {
  state: 'CONNECTED',
  cause: null
};
function subscribeStore(cb) {
  return connectivityStore.subscribe(cb);
}
function getStoreSnapshot() {
  const s = connectivityStore.getState();
  if (cachedSnapshot.state !== s.state || cachedSnapshot.cause !== s.cause) {
    cachedSnapshot = {
      state: s.state,
      cause: s.cause
    };
  }
  return cachedSnapshot;
}
// SSR fallback: connectivityStore is HMR-safe and module-level; reads
// before mount return the initial CONNECTED state.
const SERVER_SNAPSHOT = {
  state: 'CONNECTED',
  cause: null
};
function getServerSnapshot() {
  return SERVER_SNAPSHOT;
}
/**
 * Public-surface connection-state hook. See `Connection` for the locked
 * return shape. Safe to mount inside `'use client'` components in
 * Next.js; SSR import path tree-shakes to a no-op via React's
 * `useSyncExternalStore` SSR fallback.
 */
export function useGetConnection() {
  const $ = _c(6);
  const fullSnapshot = React.useSyncExternalStore(subscribeStore, getStoreSnapshot, getServerSnapshot);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      filters: {
        status: "pending"
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const pending = useMutationState(t0);
  const queuedMutations = pending.length;
  const lastFireRef = React.useRef(0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const now = Date.now();
      if (now - lastFireRef.current < RECONNECT_CLICK_DEBOUNCE_MS) {
        return;
      }
      lastFireRef.current = now;
      requestAction("reconnect", performTransportReconnect);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const forceReconnect = t1;
  let t2;
  if ($[2] !== fullSnapshot.cause || $[3] !== fullSnapshot.state || $[4] !== queuedMutations) {
    t2 = {
      state: fullSnapshot.state,
      cause: fullSnapshot.cause,
      queuedMutations,
      forceReconnect
    };
    $[2] = fullSnapshot.cause;
    $[3] = fullSnapshot.state;
    $[4] = queuedMutations;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}